import { InputHTMLAttributes, forwardRef } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { FieldFrame, controlStyle, useFieldId } from "@pages/AdminDashboard/ui/FieldFrame";

const Input = styled.input<{ $width?: number; $isPhone: boolean }>`
  ${controlStyle}
`;

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "width"> {
  label: string;
  isLabelHidden?: boolean;
  error?: string;
  width?: number;
}

// A labelled text control with the message of its error below it (FIELD-R1…R12).
// It hands its ref, name, change and blur straight to the native input, so a
// react-hook-form `register` works on it unchanged.
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, isLabelHidden, error, width, id, ...inputProps }, ref) => {
    const isPhone = useIsPhoneLayout();
    const generatedId = useFieldId();
    const controlId = id ?? generatedId;
    const errorId = `${controlId}-error`;

    return (
      <FieldFrame
        label={label}
        isLabelHidden={isLabelHidden}
        controlId={controlId}
        errorId={errorId}
        error={error}
      >
        <Input
          {...inputProps}
          ref={ref}
          id={controlId}
          $width={width}
          $isPhone={isPhone}
          aria-invalid={error ? true : inputProps["aria-invalid"]}
          aria-describedby={error ? errorId : inputProps["aria-describedby"]}
        />
      </FieldFrame>
    );
  }
);

TextField.displayName = "TextField";
